/**
 * @module DemoModeProvider
 * @purpose Fetches and refreshes platform demo-mode status (`GET /platform/status`) for the
 *   whole app -- drives the always-visible DemoModeBanner and the platform admin settings page.
 * @depends services/platform (getPlatformStatus), contexts/demo-mode-context
 * @dependedOnBy App.tsx (mounted between AuthProvider and BrowserRouter)
 *
 * @rules
 * - Performance: single shared fetch on mount, on a 60s interval, and on window focus --
 *   consumers read from context rather than each polling independently
 * - State: local useState holding the last-known PlatformStatus (or null before first success)
 * - Errors: fetch failures never throw out of the provider -- status simply keeps its
 *   last-known value (or stays null) so a transient network blip never crashes the app shell
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { getPlatformStatus } from '../services/platform'
import { DemoModeContext } from './demo-mode-context'
import { logger } from '../utils/logger'
import type { PlatformStatus } from '../types/platform'

const POLL_INTERVAL_MS = 60_000

export function DemoModeProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [status, setStatus] = useState<PlatformStatus | null>(null)
  const mountedRef = useRef(true)

  const fetchStatus = useCallback(async (): Promise<void> => {
    try {
      const result = await getPlatformStatus()
      if (mountedRef.current) {
        setStatus(result)
      }
    } catch (err) {
      logger.warn('DemoModeProvider', 'Failed to fetch platform status; keeping last-known value', err)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    void fetchStatus()

    const intervalId = setInterval(() => {
      void fetchStatus()
    }, POLL_INTERVAL_MS)

    const onFocus = (): void => {
      void fetchStatus()
    }
    window.addEventListener('focus', onFocus)

    return () => {
      mountedRef.current = false
      clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
    }
  }, [fetchStatus])

  return (
    <DemoModeContext.Provider value={{ status, refetch: fetchStatus }}>
      {children}
    </DemoModeContext.Provider>
  )
}
